import React from 'react';
import styled from '../../styled';
import { Headline } from '../Headline';
import { AnimatedNumber, Reveal } from '../motion';

const SectionWrapper = styled('section')`
  padding: 60px 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const DarkPanel = styled('div')`
  position: relative;
  background: linear-gradient(160deg, #0e1c2b 0%, #14293f 100%);
  border-radius: 24px;
  padding: 72px 48px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(
      rgba(255, 255, 255, 0.05) 1px,
      transparent 1px
    );
    background-size: 24px 24px;
    pointer-events: none;
  }

  @media (max-width: 960px) {
    padding: 48px 24px;
  }
`;

const PanelHeader = styled('div')`
  text-align: center;
  margin-bottom: 56px;
  position: relative;
`;

const PanelKicker = styled('div')`
  font-family: 'SF Mono', 'Roboto Mono', 'Fira Mono', monospace;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #69c0ff;
  margin-bottom: 16px;
`;

const PanelDescription = styled('p')`
  font-size: 17px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.75);
  max-width: 720px;
  margin: 16px auto 0;
`;

const StatsGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;
  position: relative;

  @media (max-width: 960px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Stat = styled('div')`
  text-align: center;
`;

const StatValue = styled('div')`
  font-size: 44px;
  font-weight: 800;
  color: #fff;
  line-height: 1.1;
  margin-bottom: 10px;

  @media (max-width: 960px) {
    font-size: 36px;
  }
`;

const StatLabel = styled('div')`
  font-size: 15px;
  font-weight: 700;
  color: #69c0ff;
  margin-bottom: 6px;
`;

const StatCaption = styled('div')`
  font-size: 13px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.6);
  max-width: 220px;
  margin: 0 auto;
`;

export const MoatSection = () => {
  return (
    <SectionWrapper>
      <Reveal distance={32}>
        <DarkPanel>
          <PanelHeader>
            <PanelKicker>The infrastructure underneath</PanelKicker>
            <Headline component="h2" style={{ color: '#fff' }}>
              AI is only as good as the data it runs on
            </Headline>
            <PanelDescription>
              Networks report commissions. Analytics tools report sessions.
              Affilimate runs the loop — on-page tracking, a product catalog at
              retail scale, unified analytics, and campaign execution in one
              system. That's what our agents work with.
            </PanelDescription>
          </PanelHeader>
          <StatsGrid>
            <Stat>
              <StatValue>
                <AnimatedNumber value={50} format={(n) => Math.round(n) + 'M+'} />
              </StatValue>
              <StatLabel>Product catalog</StatLabel>
              <StatCaption>
                Products matched against your links for opportunity discovery
              </StatCaption>
            </Stat>
            <Stat>
              <StatValue>
                <AnimatedNumber
                  value={100}
                  format={(n) => Math.round(n) + '+'}
                />
              </StatValue>
              <StatLabel>Network integrations</StatLabel>
              <StatCaption>
                Transactions, rates, and payouts unified on the same pages you
                monetize
              </StatCaption>
            </Stat>
            <Stat>
              <StatValue>
                <AnimatedNumber
                  value={200}
                  format={(n) => Math.round(n) + '+'}
                />
              </StatValue>
              <StatLabel>Premium publishers</StatLabel>
              <StatCaption>
                Media companies and enterprise publishers scaling on Affilimate
              </StatCaption>
            </Stat>
            <Stat>
              <StatValue>
                $<AnimatedNumber value={3} format={(n) => n.toFixed(0) + 'B+'} />
              </StatValue>
              <StatLabel>GMV delivered</StatLabel>
              <StatCaption>
                Annual GMV publishers on Affilimate deliver for brands
              </StatCaption>
            </Stat>
          </StatsGrid>
        </DarkPanel>
      </Reveal>
    </SectionWrapper>
  );
};
